import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import {ACTUAL_TECHNOLOGY_COST_ALLOCATION_EDIT} from "typeConstants/rights";
import { InitialState } from "reducers/initialState";
import {
  DataVendorCostAllocationRequest,
  DataVendorCostAllocationResponse,
  DataVendorDto,
  MajorCompanyDto,
  CustomerListModel,
  DataVendorCostAllocationRequestCustomerDetails,
  SurveyTypeListDto,
} from "interfaces/Mueller.Business.Models";
import { getMajorCompanies, searchCustomersText } from "actions/customer";
import { getAllSurveyTypes } from "actions/user";
import { getDataVendors, allocateCosts } from "requests/dataVendors";
import MSpinner from "../MSpinner";
import MDialog from "../MDialog";
import Button from "@mui/material/Button";
import MCard, { MCardControlMethods, MCardField } from "../MCard";
import * as yup from "yup";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_MONEY, DATA_TYPE_DATE, DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";
import { MSelectOption } from "../MSelect";
import TechCostCustomerSurveyTypes from "./TechCostCustomerSurveyTypes";

interface TechCostAddInvoiceProps {
  // Redux
  canEdit: boolean;
  getMajorCompanies: () => void;
  getCustomers: () => void;
  getAllSurveyTypes: () => void;
  majorCompanies: MajorCompanyDto[];
  customerList: CustomerListModel[];
  allSurveyTypes: SurveyTypeListDto[];
}

const TechCostAddInvoice = (props: TechCostAddInvoiceProps) => {
  const [currentVendorCode, setCurrentVendorCode] = useState<string | undefined>();
  const [currentResponse, setCurrentResponse] = useState<DataVendorCostAllocationResponse | undefined>();
  const [dataVendors, setDataVendors] = useState<DataVendorDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showFullSettings, setShowFullSettings] = useState<boolean>(false);
  const [customerSurveyTypeEntries, setCustomerSurveyTypeEntries] = useState<
    DataVendorCostAllocationRequestCustomerDetails[]
  >([]);
  const cardControlMethods = useRef<MCardControlMethods>();
  const mounted = useRef(true);

  useEffect(() => {
    getDataVendors(setDataVendors, mounted);
    if (!props.majorCompanies) {
      props.getMajorCompanies();
    }
    if (!props.customerList.length) {
      props.getCustomers();
    }
    if (!props.allSurveyTypes.length) {
      props.getAllSurveyTypes();
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  // Dialog

  const onHideResponse = () => setCurrentResponse(undefined);

  const dialogActions = [
    <Button onClick={onHideResponse}>
      OK
    </Button>,
  ];

  // Form

  const getVendorOperationOptions = () => {
    let options = [];
    if (currentVendorCode) {
      const vendor = dataVendors.find((v) => v.dataVendorCode === currentVendorCode);
      if (vendor) {
        options = vendor.operationTypes.map((o) => ({ label: o.operationTypeName, value: o.operationTypeCode }));
      }
    }
    return options;
  };

  const getMajorCompanyOptions = () => {
    let options = [];
    if (props.majorCompanies) {
      options = props.majorCompanies.map((m) => ({ label: m.companyName, value: m.code }));
    }
    return options;
  };

  const cardFields: MCardField[] = [
    {
      key: "dataVendorCode",
      label: "Vendor",
      validation: yup.string().required("Vendor is required"),
      value: currentVendorCode,
      dataType: DATA_TYPE_DROPDOWN2,
      onChange: (option: MSelectOption) => setCurrentVendorCode(`${option.value}`),
      dataTypeOptions: {
        options: dataVendors.map((d) => ({ label: d.dataVendorName, value: d.dataVendorCode })),
        nullable: false,
      },
    },
    {
      key: "invoiceReference",
      label: "Invoice Reference",
      value: "",
      editable: true,
      validation: yup.string().nullable().required("Invoice Reference is required"),
    },
    {
      key: "totalCost",
      dataType: DATA_TYPE_MONEY,
      label: "Total Cost",
      value: 0,
      validation: yup.number().min(0.01, "Total cost must be greater than zero"),
    },
    {
      key: "fromDate",
      label: "Starting Date",
      value: undefined,
      dataType: DATA_TYPE_DATE,
      validation: yup.date().typeError("Must be a date").required("Starting Date is required"),
    },
    {
      key: "throughDate",
      label: "Ending Date",
      value: undefined,
      dataType: DATA_TYPE_DATE,
      validation: yup.date().typeError("Must be a date").required("Ending Date is required"),
    },
    {
      key: "useVendorDefaultSettings",
      dataType: DATA_TYPE_BOOLEAN,
      label: "Use Vendor Default  Settings",
      value: true,
      onChange: () => setShowFullSettings(!showFullSettings),
    },
  ];

  if (showFullSettings) {
    cardFields.push(
      {
        key: "dataVendorOperationTypeCode",
        label: "Vendor Operation Type",
        value: undefined,
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: getVendorOperationOptions(), nullable: true },
      },
      {
        key: "onlyAllocateToCasesThatUsedTheVendor",
        dataType: DATA_TYPE_BOOLEAN,
        label: "Only Allocate to Cases that Used the Vendor",
        value: false,
      },
      // {
      //   key: "onlyAllocateToMajorCompanies",
      //   label: "Only Allocate to Major Companies (optional)",
      //   value: undefined,
      //   dataType: DATA_TYPE_DROPDOWN2,
      //   dataTypeOptions: { options: getMajorCompanyOptions(), nullable: true, isMultiSelect: true },
      // },
      // {
      //   key: "onlyAllocateToCustomerNumbers",
      //   label: "Only Allocate to Customer Numbers (optional)",
      //   value: undefined,
      //   dataType: DATA_TYPE_DROPDOWN2,
      //   dataTypeOptions: {
      //     options: props.customerList.map((c) => ({ label: `${c.number} ${c.displayName}`, value: c.number })),
      //     nullable: true,
      //     isMultiSelect: true,
      //   },
      // },
      {
        key: "onlyAllocateToSuccessfulVendorCalls",
        dataType: DATA_TYPE_BOOLEAN,
        label: "Only Allocate to Successful Vendor Calls",
        value: false,
      },
      {
        key: "onlyAllocateToChargeableVendorCalls",
        dataType: DATA_TYPE_BOOLEAN,
        label: "Only Allocate to Chargeable Vendor Calls",
        value: false,
      },
      {
        key: "OnlyAllocateOncePerDataVendorReferenceId",
        dataType: DATA_TYPE_BOOLEAN,
        label: "Only Allocate Once per Unique Vendor Reference ID",
        value: false,
      },
      {
        key: "OnlyAllocateOncePerCaseControlNumber",
        dataType: DATA_TYPE_BOOLEAN,
        label: "Only Allocate Once per Unique Case Control Number",
        value: false,
      }
    );
  }

  const onAddCustomerSurveyType = (newEntry: DataVendorCostAllocationRequestCustomerDetails) =>
    setCustomerSurveyTypeEntries((prevItems) => [...prevItems, newEntry]);
  const removeCustomerSurveyType = (index: number) =>
    setCustomerSurveyTypeEntries((prevItems) => prevItems.filter((_, i) => i !== index));

  const onSubmit = () => {
    if (!cardControlMethods.current.runValidation(true)) {
      return;
    }
    const cardData = cardControlMethods.current.getEditedDataObject();
    const request: DataVendorCostAllocationRequest = {
      dataVendorCode: cardData.dataVendorCode,
      invoiceReference: cardData.invoiceReference,
      totalCost: parseFloat(cardData.totalCost),
      //@ts-ignore
      fromDate: new Date(cardData.fromDate),
      //@ts-ignore
      throughDate: new Date(cardData.throughDate),
      dataVendorOperationTypeCode: cardData.dataVendorOperationTypeCode,
      onlyAllocateToCasesThatUsedTheVendor: !!cardData.onlyAllocateToCasesThatUsedTheVendor,
      onlyAllocateToMajorCompanies: [], // TODO? Not sure if we're using these after adding the weight by customer survey type, etc
      onlyAllocateToCustomerNumbers: [], // TODO?
      onlyAllocateToSuccessfulVendorCalls: !!cardData.onlyAllocateToSuccessfulVendorCalls,
      onlyAllocateToChargeableVendorCalls: !!cardData.onlyAllocateToChargeableVendorCalls,
      onlyAllocateOncePerDataVendorReferenceId: !!cardData.onlyAllocateOncePerDataVendorReferenceId,
      onlyAllocateOncePerCaseControlNumber: !!cardData.onlyAllocateOncePerCaseControlNumber,
      customerDetails: customerSurveyTypeEntries,
      useVendorDefaultSettings: !!cardData.useVendorDefaultSettings,
    };
    setLoading(true);
    const onSuccess = (response: DataVendorCostAllocationResponse) => {
      setCurrentResponse(response);
      setCurrentVendorCode(undefined);
      setShowFullSettings(false);
      setCustomerSurveyTypeEntries([]);
      setLoading(false);
    };
    const onError = () => setLoading(false);
    allocateCosts(request, onSuccess, onError, mounted);
  };

  // Render

  if (loading || !props.majorCompanies || !props.customerList.length || !dataVendors.length) {
    return <MSpinner />;
  }

  return (
    <>
      <MCard
        key="tech-cost-add-invoice"
        title="Add Invoice"
        onlyEditable
        fields={cardFields}
        getControlMethods={(controlMethods) => (cardControlMethods.current = controlMethods)}
      >
        {showFullSettings && (
          <TechCostCustomerSurveyTypes
            entries={customerSurveyTypeEntries}
            onAdd={onAddCustomerSurveyType}
            onDelete={removeCustomerSurveyType}
          />
        )}
        <div>
          <Button onClick={onSubmit} disabled={!props.canEdit}>
            Submit
          </Button>
        </div>
      </MCard>
      {currentResponse !== undefined && (
        <MDialog
          id="confirm-delete-survey-type-requirements"
          className="confirm-delete-survey-type-requirements"
          title="Invoice Successfully Added"
          visible={currentResponse !== undefined}
          contentActions={dialogActions}
          focusOnMount={false}
          width={525}
        >
          <p>Cost allocation records created: {currentResponse.costAllocationCount} </p>
          <p>Applied to {currentResponse.casesAllocatedCount} cases</p>
        </MDialog>
      )}
    </>
  );
};

const mapStateToProps = (state: InitialState) => ({
  canEdit: userHasRight(ACTUAL_TECHNOLOGY_COST_ALLOCATION_EDIT)(state),
  majorCompanies: state.customer.majorCompanies,
  customerList: state.customer.customerList,
  allSurveyTypes: state.user.allSurveyTypes,
});

const mapDispatchToProps = (dispatch) => ({
  getMajorCompanies() {
    dispatch(getMajorCompanies());
  },
  getCustomers() {
    dispatch(searchCustomersText()); // Searching without a search term will get all customers
  },
  getAllSurveyTypes() {
    dispatch(getAllSurveyTypes());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TechCostAddInvoice);
